import { ButtonBase, FormControl, makeStyles, MenuItem, TextField } from '@material-ui/core';
import React, { ChangeEvent } from 'react';

import { useResponsiveFlags } from '../../../shared/lib';

const THEME = {
  borderColor: 'rgb(242, 245, 248)',
  activeBackgroundColor: 'rgb(242, 245, 248)',
  borderRadius: 10,
  fontSize: 16,
  fontWeight: 400,
  activeFontWeight: 500,
  padding: '10px 22px',
} as const;

const textColor = '#181F28';

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginTop: 0,
    marginBottom: 0,
    minWidth: 0,
    maxWidth: '400px',
    flex: '1 1 auto',
    // @todo Перейти на использование useResponsiveFlags (Здесь и в других местах в стилях)?
    [theme.breakpoints.down(960)]: {
      maxWidth: '400px',
    },
    [theme.breakpoints.down(750)]: {
      maxWidth: '300px',
    },
    [theme.breakpoints.down(600)]: {
      maxWidth: '200px',
    },
    [theme.breakpoints.down(480)]: {
      maxWidth: '150px',
    },
    '& svg': {
      color: textColor,
    },
  },
  input: {
    color: textColor,
    fontWeight: 500,
    minWidth: 0,
    fontSize: 14,
    [theme.breakpoints.only('sm')]: {
      fontSize: 15,
    },
    [theme.breakpoints.up('md')]: {
      fontSize: 16,
    },
    '&:before': {
      borderBottomColor: textColor,
    },
    '&:after': {
      borderBottomColor: textColor,
    },
    '& .MuiSelect-root': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  list: {
    '& li': {
      color: textColor,
    },
    '& li:last-child': {
      color: textColor,
    },
    '& li.Mui-selected': {
      backgroundColor: `#F0F1F4 !important`,
      color: textColor,
    },
    '& li:hover': {
      backgroundColor: `#F0F1F4 !important`,
      color: textColor,
    },
    backgroundColor: '#FFF',
  },
}));

const getButtonStyles = (isActive: boolean, isFirst: boolean, isLast: boolean) => ({
  fontWeight: isActive ? THEME.activeFontWeight : THEME.fontWeight,
  fontSize: THEME.fontSize,
  padding: THEME.padding,
  backgroundColor: isActive ? THEME.activeBackgroundColor : undefined,
  borderTopLeftRadius: isFirst ? THEME.borderRadius : 0,
  borderBottomLeftRadius: isFirst ? THEME.borderRadius : 0,
  borderTopRightRadius: isLast ? THEME.borderRadius : 0,
  borderBottomRightRadius: isLast ? THEME.borderRadius : 0,
  borderRight: isLast ? 'none' : `1px solid ${THEME.borderColor}`,
});

export const SchemaSwitcherWidget = ({
  sections,
  onSelectSection,
  sectionId,
}: {
  sections: GM.ReservesSchemas.SectionDTO[];
  onSelectSection: (sectionId: number) => void;
  sectionId?: number;
}) => {
  const { isDesktop } = useResponsiveFlags();
  const classes = useStyles();

  const handleSectionClick = (clickedSectionId: number) => {
    if (clickedSectionId !== sectionId) {
      onSelectSection(clickedSectionId);
    }
  };

  const handleSelectChange = (e: ChangeEvent<{ name?: string; value: unknown }>) => {
    const value = e.target.value;
    if (value !== null && value !== undefined && value !== '') {
      const newSectionId = typeof value === 'number' ? value : parseInt(String(value), 10);
      if (!isNaN(newSectionId) && newSectionId > 0 && newSectionId !== sectionId) {
        onSelectSection(newSectionId);
      }
    }
  };

  if (!isDesktop) {
    return (
      <FormControl fullWidth classes={{ root: classes.formControl }}>
        <TextField
          value={sectionId ?? ''}
          onChange={handleSelectChange}
          select
          id="select-section"
          InputProps={{ classes: { root: classes.input } }}
          fullWidth
          SelectProps={{
            MenuProps: {
              classes: {
                list: classes.list,
              },
              getContentAnchorEl: null,
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
            },
          }}
        >
          {sections.map((section) => (
            <MenuItem key={section.id} value={section.id}>
              {section.name}
            </MenuItem>
          ))}
        </TextField>
      </FormControl>
    );
  }

  return (
    <div
      style={{
        display: 'inline-flex',
        border: `1px solid ${THEME.borderColor}`,
        borderRadius: THEME.borderRadius,
      }}
    >
      {sections.map((section, index) => {
        const isActive = section.id === sectionId;
        const isFirst = index === 0;
        const isLast = index === sections.length - 1;

        return (
          <ButtonBase
            key={section.id}
            disabled={isActive}
            onClick={() => handleSectionClick(section.id)}
            className="design-v3-font"
            style={getButtonStyles(isActive, isFirst, isLast)}
          >
            {section.name}
          </ButtonBase>
        );
      })}
    </div>
  );
};
